/**
 * Static geometry of Ukrainian oblasts for matching the user's GPS position against
 * per-oblast air-raid alert states (ubilling aerialalerts API, proxied by the backend).
 *
 * An alert is considered relevant when the user is inside an oblast's bounding box
 * or within `ALERT_RADIUS_KM` of it — sirens and threats do not stop at oblast
 * borders, so a generous buffer beats exact polygon resolution here.
 */

/** [minLat, minLon, maxLat, maxLon] */
export type OblastBbox = [number, number, number, number];

export interface OblastInfo {
  /** Exact key as returned by the alerts API, e.g. "Харківська область", "м. Київ". */
  name: string;
  bbox: OblastBbox;
}

/** How far (km) outside an oblast's bbox its alert still applies to the user. */
export const ALERT_RADIUS_KM = 50;

/**
 * Approximate bounding boxes (WGS84). Coarse on purpose: combined with the 50 km
 * buffer the worst-case error of a bbox vs the true border is negligible for the
 * "is there an alert near me" question.
 */
export const OBLASTS: OblastInfo[] = [
  { name: 'Вінницька область', bbox: [47.95, 27.40, 49.85, 30.05] },
  { name: 'Волинська область', bbox: [50.30, 23.60, 51.95, 26.10] },
  { name: 'Дніпропетровська область', bbox: [47.45, 32.95, 49.20, 36.95] },
  { name: 'Донецька область', bbox: [46.85, 36.20, 49.25, 39.05] },
  { name: 'Житомирська область', bbox: [49.60, 27.20, 51.70, 29.80] },
  { name: 'Закарпатська область', bbox: [47.90, 22.10, 49.10, 24.65] },
  { name: 'Запорізька область', bbox: [46.20, 34.05, 48.05, 37.25] },
  { name: 'Івано-Франківська область', bbox: [47.70, 23.50, 49.55, 25.65] },
  { name: 'Київська область', bbox: [49.15, 29.25, 51.55, 32.15] },
  { name: 'Кіровоградська область', bbox: [47.75, 29.70, 49.25, 33.30] },
  { name: 'Луганська область', bbox: [47.80, 37.80, 50.10, 40.25] },
  { name: 'Львівська область', bbox: [48.70, 22.65, 50.65, 25.45] },
  { name: 'Миколаївська область', bbox: [46.40, 30.20, 48.25, 33.20] },
  { name: 'Одеська область', bbox: [45.20, 28.20, 48.25, 31.40] },
  { name: 'Полтавська область', bbox: [48.75, 32.05, 50.55, 35.50] },
  { name: 'Рівненська область', bbox: [50.00, 25.05, 51.95, 27.75] },
  { name: 'Сумська область', bbox: [50.00, 32.85, 52.40, 35.65] },
  { name: 'Тернопільська область', bbox: [48.50, 24.70, 50.25, 26.45] },
  { name: 'Харківська область', bbox: [48.65, 34.85, 50.45, 38.10] },
  { name: 'Херсонська область', bbox: [45.70, 31.50, 47.60, 35.30] },
  { name: 'Хмельницька область', bbox: [48.45, 26.10, 50.60, 27.90] },
  { name: 'Черкаська область', bbox: [48.45, 29.60, 50.10, 32.90] },
  { name: 'Чернівецька область', bbox: [47.75, 24.90, 48.70, 27.55] },
  { name: 'Чернігівська область', bbox: [50.30, 30.50, 52.40, 33.55] },
  { name: 'м. Київ', bbox: [50.20, 30.20, 50.65, 30.85] },
];

const KM_PER_DEG_LAT = 111.32;

/** Distance (km) from a point to a bbox; 0 when the point is inside. */
export function distanceToBboxKm(lat: number, lon: number, bbox: OblastBbox): number {
  const [minLat, minLon, maxLat, maxLon] = bbox;
  const dLatDeg = Math.max(minLat - lat, 0, lat - maxLat);
  const dLonDeg = Math.max(minLon - lon, 0, lon - maxLon);
  const dLatKm = dLatDeg * KM_PER_DEG_LAT;
  const dLonKm = dLonDeg * KM_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
  return Math.hypot(dLatKm, dLonKm);
}

/**
 * Oblasts whose territory is within `radiusKm` of the user, nearest first.
 * "м. Київ" lies inside "Київська область", so a point in Kyiv matches both —
 * intended: a city alert and an oblast alert are both relevant there.
 */
export function nearbyOblasts(lat: number, lon: number, radiusKm = ALERT_RADIUS_KM): OblastInfo[] {
  return OBLASTS
    .map((oblast) => ({ oblast, dist: distanceToBboxKm(lat, lon, oblast.bbox) }))
    .filter(({ dist }) => dist <= radiusKm)
    .sort((a, b) => a.dist - b.dist)
    .map(({ oblast }) => oblast);
}
